import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReportExportType } from '@app/enums';
import { getPermissionState } from '@app/reducers/auth/permission.helper';
import { Store } from '@ngrx/store';
import { AppState } from '@reducers/interfaces';
import { ReportStatus, ReportType } from '@reducers/orm/reports/report.model';
import { ReportService } from '@reducers/orm/reports/report.service';
import {
  catchError,
  EMPTY,
  exhaustMap,
  filter,
  finalize,
  map,
  retry,
  Subject,
  switchMap,
  take,
  takeWhile,
  tap,
  timeout,
  TimeoutError,
  timer,
} from 'rxjs';

import {
  payrollReportData,
  payrollReportFilters,
  reportableDepartmentIds,
  type PayrollReportData,
} from './payroll-report';
import { PayrollRunService } from './payroll-run.service';
import { type PayrollRunScope } from './payroll.model';

/** How often the report's status is re-read while it generates. */
const pollIntervalMs = 1_000;

/** A single failed status read is a blip, not an answer: the report is still generating. */
const pollRetryCount = 2;

/**
 * How long the manager is held behind the spinner before the wait is abandoned, measured from the
 * moment they press Generate so it covers the request as well as the poll. Generation is normally
 * seconds, so this is not a realistic completion time: it is the point past which blocking the
 * flow is worse than handing the manager back their step. The report itself is unaffected and
 * still lands in the reports list, which is what the timeout's message says.
 */
const pollTimeoutMs = 120_000;

export type PayrollReportFailure =
  /** The request to generate was rejected, so nothing is being generated. */
  | 'request'
  /** The backend reached a terminal status that is not FINISHED, so no report exists. */
  | 'failed'
  /** Requested successfully, but we lost track of it. It may still be generating. */
  | 'poll'
  | 'timeout'
  /** No department in the run's scope may be reported on, so there is nothing to request. */
  | 'no-reportable-departments';

/**
 * Each non-idle phase carries the scope it belongs to, which is what makes the state
 * self-invalidating. See `state`.
 *
 * A generated report is never 'finished' on its own: generation flows straight into the fetch
 * that puts its rows on the screen, so the three phases past 'generating' all mean the report
 * exists and is saved in Reports. Only 'failed' means no report was produced.
 */
export type PayrollReportState =
  | { phase: 'idle' }
  | { phase: 'generating'; scope: PayrollRunScope }
  | { phase: 'fetching'; scope: PayrollRunScope; reportId: string }
  | { phase: 'ready'; scope: PayrollRunScope; reportId: string; report: PayrollReportData }
  /**
   * Generated, but its contents could not be read back. A separate phase rather than a
   * `PayrollReportFailure` reason, because the four of those all mean no report exists and invite
   * a retry — here one does, and generating again would only earn the manager a duplicate.
   */
  | { phase: 'fetch-failed'; scope: PayrollRunScope; reportId: string }
  | { phase: 'failed'; scope: PayrollRunScope; reason: PayrollReportFailure };

/**
 * Generates the payroll report for a run, waits for it to finish, and reads its contents back so
 * the step can render them.
 *
 * A plain signals service rather than a signal store: one request, one status poll, one fetch and
 * no entity collection of its own. The requested report is already added to the reports entity
 * state by `ReportService.triggerReport`.
 *
 * State lives here rather than in the step component because the step is rendered inside the
 * payroll page's `@switch` and is destroyed the moment the manager pages back to step four. Held
 * here, a run that is still generating survives that and is still generating when they return, so
 * the action cannot be triggered a second time for the same run — and a report already fetched is
 * still in hand when they come back to it, rather than being fetched again.
 */
@Injectable({ providedIn: 'root' })
export class PayrollReportService {
  #reportService = inject(ReportService);
  #run = inject(PayrollRunService);
  #store = inject<Store<AppState>>(Store);

  #permissions = this.#store.selectSignal(getPermissionState);

  #rawState = signal<PayrollReportState>({ phase: 'idle' });

  /**
   * The state, but only while it still describes the run in progress. A report belongs to the
   * scope it was requested for, so rather than imperatively resetting on every scope change (an
   * effect that once cancelled the very request it was meant to protect, because an effect's first
   * run is deferred), the scope is carried in the state and compared on read. A re-scoped or
   * abandoned run reads `idle` without anything having to fire in between.
   */
  state = computed<PayrollReportState>(() => {
    const current = this.#rawState();

    if (current.phase === 'idle') {
      return current;
    }

    return current.scope === this.#run.scope() ? current : { phase: 'idle' };
  });

  isGenerating = computed(() => this.state().phase === 'generating');

  /**
   * Whether this run has already produced its report — true from the moment generation finishes,
   * whether or not its contents could then be read back. Scoped like `state` itself, so a
   * re-scoped run reads `false` again.
   *
   * Read by `payrollExitGuard`, which lets a departure past without confirming: the thing the run
   * existed to produce is saved and listed in Reports, so warning that the run is discarded would
   * be alarming on the flow's own success path, and untrue about the report itself.
   */
  hasGeneratedReport = computed(() => generatedPhases.has(this.state().phase));

  /** The fetched report, or null until one is in hand for the run the manager is looking at. */
  report = computed(() => {
    const current = this.state();

    return current.phase === 'ready' ? current.report : null;
  });

  /** True between the report finishing and its contents arriving, which the step renders as a loading table. */
  isFetchingReport = computed(() => this.state().phase === 'fetching');

  #downloading = signal<ReportExportType | null>(null);

  /** The export being written, so its control can show the wait and neither can be pressed twice. */
  downloading = this.#downloading.asReadonly();

  /**
   * The run's departments that the payroll report may actually be requested for. Exposed because
   * the step has to say what a run covers *before* the manager presses anything: an empty list
   * means there is nothing to generate, and a shorter list than the run's own means the report
   * will be narrower than the period they set up.
   */
  reportableDepartments = computed(() => {
    const scope = this.#run.scope();

    return scope ? reportableDepartmentIds(scope.departmentIds, this.#permissions()) : [];
  });

  // The departments travel with the request rather than being recomputed downstream, so the set
  // that was checked is the set that gets sent.
  #requests = new Subject<{ scope: PayrollRunScope; departmentIds: string[] }>();

  constructor() {
    this.#requests
      .pipe(
        switchMap((request) => this.#generate(request.scope, request.departmentIds)),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  /**
   * Requests the report for the run's current scope, then drives `state` to 'ready' — or to
   * 'failed' or 'fetch-failed', depending on which half did not get there.
   * A call made while one is already generating is dropped rather than queued: the step's action is
   * replaced by the loading indicator, so a second call can only come from somewhere that has lost
   * track of the first.
   *
   * The scope is read here rather than passed in, so the scope recorded in `state` is always the
   * one `state` compares against. A caller handing in a scope it captured earlier — before an
   * `await`, or before a dialog it opened — could otherwise record a report against a run that has
   * already moved on, which `state` would then mask as idle.
   */
  generate(): void {
    const scope = this.#run.scope();

    if (!scope || this.isGenerating()) {
      return;
    }

    const departmentIds = this.reportableDepartments();

    // The backend would reject a request with no reportable department as a 403, which would reach
    // the manager as a generic failure inviting a retry that can never succeed. The step hides the
    // action in this case, so reaching here means a caller that did not check first.
    if (departmentIds.length === 0) {
      this.#rawState.set({ phase: 'failed', scope, reason: 'no-reportable-departments' });
      return;
    }

    this.#rawState.set({ phase: 'generating', scope });
    this.#requests.next({ scope, departmentIds });
  }

  /**
   * Saves the shown report as a file, through the same call the reports page makes — `ReportApi`
   * saves the response itself, so there is nothing here to do with the result.
   *
   * Lives here rather than with the buttons because the two are no longer in the same component:
   * the report is rendered by the step and exported from the page header. Reading `reportId` off
   * the state also means an export can only ever be for the report actually on screen.
   *
   * No `ignoreMetaMessage`, unlike `#fetch`: a failed download has nothing on screen to explain
   * it, so `ErrorInterceptor`'s toast is the only thing that would tell the manager at all.
   */
  download(exportType: ReportExportType): void {
    const current = this.state();

    if (current.phase !== 'ready' || this.#downloading()) {
      return;
    }

    this.#downloading.set(exportType);

    this.#reportService
      .fetchReport(current.reportId, exportType)
      .pipe(
        take(1),
        finalize(() => this.#downloading.set(null)),
      )
      .subscribe();
  }

  #generate(scope: PayrollRunScope, departmentIds: string[]) {
    // Which half failed decides what the manager is told: before the request lands nothing is
    // being generated, after it the report exists and will turn up in Reports regardless.
    let reportRequested = false;

    return this.#reportService
      .triggerReport({ type: ReportType.PAYROLL, options: payrollReportFilters(scope, departmentIds) })
      .pipe(
        tap(() => {
          reportRequested = true;
        }),
        switchMap((requestedReport) =>
          // The id comes from the trigger response, not the status response: `ReportStatusModel` is
          // a declared shape over an untyped gateway, so its `id` is an assertion rather than a
          // guarantee, and this one is already load-bearing.
          this.#awaitCompletion(requestedReport.id, scope).pipe(
            map((terminalStatus) => ({ reportId: requestedReport.id, status: terminalStatus.status })),
          ),
        ),
        // Spans the request as well as the poll. Inside `#awaitCompletion` it would leave a hung
        // `reports/request` POST uncovered, and `ApiGateway` sets no HTTP timeout of its own, so
        // the spinner would stay up forever with `generate()`'s guard blocking every retry.
        timeout(pollTimeoutMs),
        // A terminal status that is not FINISHED is an *answer*, not a lost poll, so it is branched
        // here rather than thrown: an error would be indistinguishable from a transport failure and
        // would tell the manager their report is on its way when no report exists.
        switchMap(({ reportId, status }) => {
          if (status !== ReportStatus.FINISHED) {
            this.#rawState.set({ phase: 'failed', scope, reason: 'failed' });
            return EMPTY;
          }

          this.#rawState.set({ phase: 'fetching', scope, reportId });
          return this.#fetch(reportId, scope);
        }),
        // Generation only: `#fetch` settles its own failure, so nothing from the fetch reaches
        // here. Reporting one of those as a `PayrollReportFailure` would tell the manager to
        // generate a report they already have.
        catchError((error: unknown) => {
          this.#rawState.set({ phase: 'failed', scope, reason: failureReason(error, reportRequested) });
          return EMPTY;
        }),
      );
  }

  /**
   * Reads a finished report's contents back for the step to render.
   *
   * Deliberately outside `pollTimeoutMs`, which is placed to cover the wait for a report that does
   * not exist yet. This one does, and is listed in Reports whatever happens here, so there is no
   * spinner worth abandoning the manager out of.
   *
   * `ignoreMetaMessage` because the step reports this failure itself, and says the report is safe
   * in Reports. `ErrorInterceptor`'s generic "An error has occurred" toast alongside that would
   * tell the manager the opposite of the message they are reading.
   */
  #fetch(reportId: string, scope: PayrollRunScope) {
    return this.#reportService
      .fetchReport(reportId, ReportExportType.HTML, { headers: { ignoreMetaMessage: true } })
      .pipe(
        tap((response) => {
          this.#rawState.set({ phase: 'ready', scope, reportId, report: payrollReportData(response) });
        }),
        catchError(() => {
          this.#rawState.set({ phase: 'fetch-failed', scope, reportId });
          return EMPTY;
        }),
      );
  }

  /**
   * Polls the report's status until it reaches a terminal one and emits that status, whatever it
   * is. Deciding what a terminal status *means* belongs to the caller: only a read failure should
   * look like a read failure. PENDING and PROCESSING keep the poll going; anything else is treated
   * as terminal, so an unrecognised future status ends the wait rather than polling forever, and
   * the caller reports it as a failed generation rather than as a report on its way.
   *
   * `exhaustMap` rather than `switchMap`: a status request slower than the interval would
   * otherwise be cancelled by the next tick, and the poll could never resolve. The `retry` keeps a
   * single failed read from ending a wait for a report that is generating perfectly well.
   */
  #awaitCompletion(reportId: string, scope: PayrollRunScope) {
    return timer(0, pollIntervalMs).pipe(
      // Stops polling for a run the manager has moved off, checked per tick rather than driven by
      // an effect: an effect's first run is deferred, so it can land *after* a generate() the
      // manager has already triggered and cancel the very request it exists to clean up.
      takeWhile(() => this.#run.scope() === scope),
      exhaustMap(() =>
        this.#reportService.getReportStatus(reportId).pipe(retry({ count: pollRetryCount, delay: pollIntervalMs })),
      ),
      filter((reportStatus) => isTerminal(reportStatus.status)),
      take(1),
    );
  }
}

/** The phases in which the run has produced a report, whatever became of its contents. */
const generatedPhases = new Set<PayrollReportState['phase']>(['fetching', 'ready', 'fetch-failed']);

function isTerminal(status: ReportStatus): boolean {
  return status !== ReportStatus.PENDING && status !== ReportStatus.PROCESSING;
}

function failureReason(error: unknown, reportRequested: boolean): PayrollReportFailure {
  if (error instanceof TimeoutError) {
    return 'timeout';
  }

  return reportRequested ? 'poll' : 'request';
}
